import {
  AnalyticsDashboard,
  CreateDashboardRequest,
  CreateWidgetRequest,
  DashboardListParams,
  DashboardWidget,
  DatabaseSchema,
  QueryExecutionRequest,
  QueryResult,
  UpdateDashboardRequest,
  UpdateWidgetRequest,
  WidgetListParams,
} from "@/types/analytics";
import { Page } from "@/types/pagination";

import { kyInstance, requestData } from "@/api/api-config";

// Dashboard API Functions
export async function listDashboards(
  params?: DashboardListParams,
): Promise<Page<AnalyticsDashboard> | null> {
  return await requestData<Page<AnalyticsDashboard>>(
    "analytics/dashboards",
    "get",
    { searchParams: params },
  );
}

export async function createDashboard(
  data: CreateDashboardRequest,
): Promise<AnalyticsDashboard | null> {
  return await requestData<AnalyticsDashboard>("analytics/dashboards", "post", {
    json: data,
  });
}

export async function updateDashboard(
  id: string,
  data: UpdateDashboardRequest,
): Promise<AnalyticsDashboard | null> {
  return await requestData<AnalyticsDashboard>(
    `analytics/dashboards/${id}`,
    "put",
    {
      json: data,
    },
  );
}

export async function deleteDashboard(id: string): Promise<void | null> {
  return await requestData<void>(`analytics/dashboards/${id}`, "delete");
}

/**
 * Every widget on a custom dashboard as one CSV, a titled section per chart —
 * the same tables a scheduled email of the dashboard carries. The range only
 * applies to widgets that follow the dashboard's date range.
 */
export async function exportCustomDashboard(
  id: string,
  params: { tz: string; startDate?: string; endDate?: string },
): Promise<Blob> {
  const response = await kyInstance.get(`analytics/dashboards/${id}/export`, {
    searchParams: Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined),
    ) as Record<string, string>,
    // Each widget's SQL gets up to 30s server-side, four widgets at a time.
    timeout: 300000,
  });
  return await response.blob();
}

// Widget API Functions
export async function listWidgets(
  params?: WidgetListParams,
): Promise<DashboardWidget[] | null> {
  return await requestData<DashboardWidget[]>("analytics/widgets", "get", {
    searchParams: { ...params },
  });
}

export async function getWidget(id: string): Promise<DashboardWidget | null> {
  return await requestData<DashboardWidget>(`analytics/widgets/${id}`, "get");
}

export async function createWidget(
  data: CreateWidgetRequest,
): Promise<DashboardWidget | null> {
  return await requestData<DashboardWidget>("analytics/widgets", "post", {
    json: data,
  });
}

export async function updateWidget(
  id: string,
  data: UpdateWidgetRequest,
): Promise<DashboardWidget | null> {
  return await requestData<DashboardWidget>(`analytics/widgets/${id}`, "put", {
    json: data,
  });
}

export async function deleteWidget(id: string): Promise<void | null> {
  return await requestData<void>(`analytics/widgets/${id}`, "delete");
}

export async function executeQuery(
  data: QueryExecutionRequest,
): Promise<QueryResult | null> {
  return await requestData<QueryResult>(
    "analytics/widgets/execute-query",
    "post",
    {
      json: data,
    },
  );
}

// Recipe replay + one bounded recovery + replacement validation can take 150s.
// POST is not retried by ky; leave headroom without multiplying execution.
export const WIDGET_EXECUTION_TIMEOUT_MS = 165_000;

export async function executeWidget(
  widgetId: string,
  params?: {
    page?: number;
    perPage?: number;
    tz?: string;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    search?: string;
    /** Inclusive ISO date (YYYY-MM-DD). Ignored server-side unless the widget is dateFilterable. */
    startDate?: string;
    /** Inclusive ISO date (YYYY-MM-DD). Ignored server-side unless the widget is dateFilterable. */
    endDate?: string;
  },
  /**
   * Override the widget execution budget when the caller knows it is a
   * legacy query. The default covers recipe replay, recovery and validation,
   * including previews and ordinary dashboard requests.
   */
  timeoutMs: number = WIDGET_EXECUTION_TIMEOUT_MS,
): Promise<QueryResult | null> {
  return await requestData<QueryResult>(
    `analytics/widgets/${widgetId}/execute`,
    "post",
    { json: params ?? {} },
    true,
    timeoutMs,
  );
}

// Schema API Functions
export async function getAnalyticsSchema(): Promise<DatabaseSchema | null> {
  return await requestData<DatabaseSchema>("analytics/schema");
}
